import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import service from '../service';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth';
import { useMessages } from './messages';
import {
  organizeInboxesByRoles,
  filterInboxesByPermissions,
  getRoleInboxes,
} from '../components/Inbox/utils';
import { getThreadParticipantName } from '../utils/emailThread';

// Create the context
const InboxContext = createContext();

// Email conversations are EmailThread records served by /v1/email-thread, not
// the address-grouped shape the SMS and APP channels use. The list view leaves
// the message collections out; they are loaded when a thread is opened.
const EMAIL_THREAD_LIST_INCLUDE = 'fish.fishProfiles,user,inbox.user';
const EMAIL_THREAD_DETAIL_INCLUDE =
  'fish.fishProfiles,user,inbox.user,inboundMessages,emailDeliveries.outboundMessage';
const EMAIL_THREAD_PAGE_SIZE = 100;

const fetchEmailThreads = async (inboxIds) => {
  if (!inboxIds || inboxIds.length === 0) return [];

  const result = await service.getEmailThreads({
    'filter[inboxId]': inboxIds.join(','),
    include: EMAIL_THREAD_LIST_INCLUDE,
    sort: '-updatedAt',
    'page[size]': String(EMAIL_THREAD_PAGE_SIZE),
  });

  return result?.data ?? [];
};

const fetchEmailThreadDetail = async (threadId) =>
  service.getEmailThread(threadId, { include: EMAIL_THREAD_DETAIL_INCLUDE });

// Provider component
export const InboxProvider = ({ children }) => {
  // =========================================================================
  // 1. STATE DECLARATIONS
  // =========================================================================

  // URL and routing state
  const [searchParams, setSearchParams] = useSearchParams();
  const inboxId = searchParams.get('inboxId');
  const roleIdParam = searchParams.get('roleId');
  const [selectedRoleId, setSelectedRoleId] = useState(roleIdParam);

  // Inbox data state
  const [inbox, setInbox] = useState(null);
  const [filteredInboxes, setFilteredInboxes] = useState([]);
  const [roleInboxes, setRoleInboxes] = useState([]);

  // Thread and message state
  const [threads, setThreads] = useState([]);
  const [selectedFishThread, setSelectedFishThread] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);

  // Channel state for multi-channel messaging
  const [selectedChannel, setSelectedChannel] = useState('SMS');

  // Ref to track current fetch request and prevent stale updates
  const fetchRequestIdRef = useRef(0);

  // Ref to dedupe "all inboxes" role fetches — avoids re-fetching the same
  // (roleId, channel) combination when unrelated state (e.g. filteredInboxes.length)
  // changes and causes effects to re-run.
  const lastAllInboxesFetchRef = useRef(null);

  // The conversation named in the URL when the Inbox mounted. It is reopened
  // once the thread list holding it has loaded, then cleared so that leaving a
  // conversation is not immediately undone by the restore.
  const threadToRestoreRef = useRef({
    id: searchParams.get('threadId'),
    inboxId: searchParams.get('threadInboxId'),
  });

  // =========================================================================
  // 2. CONTEXT HOOKS
  // =========================================================================

  const { user } = useAuth();
  const {
    setSmsThreadMessages,
    fetchAllFish,
    inboxes,
    fetchAllInboxes,
    userPermissions,
    fetchUserPermissions,
  } = useMessages();

  const queryClient = useQueryClient();

  // =========================================================================
  // 3. DERIVED DATA
  // =========================================================================

  // Organize inboxes by roles
  const { roles, inboxesByRole, individualInboxes } = organizeInboxesByRoles(
    userPermissions,
    filteredInboxes,
  );

  // Phone-shaped searches such as `(480) 205-4493` match stored E.164 numbers by their digits.
  const searchDigits = searchQuery?.replace(/\D/g, '');
  const phoneSearch =
    searchDigits && /^[\d\s()+\-.]+$/.test(searchQuery) ? searchDigits : searchQuery;

  // Filter threads based on search query
  const filteredThreads = threads.filter((thread) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();

    // Handle APP thread structure
    if (selectedChannel === 'APP' || thread.method === 'APP') {
      const participantName = thread.otherParticipant?.name || '';
      const fishName =
        thread.otherParticipant?.fish?.fishProfiles?.[0]?.fName || '';
      const fishLName =
        thread.otherParticipant?.fish?.fishProfiles?.[0]?.lName || '';

      return (
        participantName.toLowerCase().includes(searchLower) ||
        fishName.toLowerCase().includes(searchLower) ||
        fishLName.toLowerCase().includes(searchLower) ||
        (fishName.toLowerCase() + ' ' + fishLName.toLowerCase()).includes(
          searchLower,
        )
      );
    }

    // Handle EMAIL thread structure
    if (selectedChannel === 'EMAIL') {
      const emailProfile =
        thread.fish?.fishProfiles?.find((fp) => fp.profileType === 'MEMBER') ||
        thread.fish?.fishProfiles?.[0];
      const address = emailProfile?.email || thread.user?.email || '';

      return (
        thread.subject?.toLowerCase().includes(searchLower) ||
        getThreadParticipantName(thread).toLowerCase().includes(searchLower) ||
        address.toLowerCase().includes(searchLower)
      );
    }

    // Handle SMS thread structure
    const profile =
      thread.fishProfiles && thread.fishProfiles.length > 0
        ? thread.fishProfiles[0]
        : { fName: thread.fName, lName: thread.lName };

    const fName = profile.fName || '';
    const lName = profile.lName || '';

    return (
      fName.toLowerCase().includes(searchLower) ||
      lName.toLowerCase().includes(searchLower) ||
      (fName.toLowerCase() + ' ' + lName.toLowerCase()).includes(searchLower) ||
      thread.phoneNumber?.includes(phoneSearch)
    );
  });

  // =========================================================================
  // 4. DATA FETCHING EFFECTS
  // =========================================================================

  // Main data fetching effect - runs when inboxId, user, or searchParams change
  useEffect(() => {
    // Increment request ID to track this specific fetch
    const currentRequestId = ++fetchRequestIdRef.current;

    // Helper to check if this request is still valid
    const isStale = () => currentRequestId !== fetchRequestIdRef.current;

    const fetchData = async () => {
      setThreadsLoading(true);

      try {
        // Group resolution below reads these results, not the render's
        // `inboxes` / `userPermissions`, which predate them on a page load.
        const [allInboxes, permissions] = await Promise.all([
          fetchAllInboxes(),
          user?.id ? fetchUserPermissions(user.id) : null,
        ]);

        if (roleIdParam) setSelectedRoleId(roleIdParam);

        // Fetch the inbox details
        if (inboxId) {
          const inboxData = await service.readInbox(inboxId);
          if (isStale()) return; // Abort if request is stale
          setInbox(inboxData);
        } else if (filteredInboxes.length > 0 && !roleIdParam) {
          // No inbox in the URL: default to the inbox this user owns, falling
          // back to the first available. (Targeted inboxes are in the list too,
          // so the first entry is not necessarily the user's own.)
          const defaultInbox =
            filteredInboxes.find((i) => i.userId === user?.id) ??
            filteredInboxes[0];
          setSearchParams({ inboxId: defaultInbox.id });
          return;
        }

        // Fetch threads for this inbox or all inboxes in the role
        if (inboxId) {
          // Selecting an individual inbox exits "all inboxes" mode — clear the
          // dedup guard so returning to "all inboxes" later re-fetches.
          lastAllInboxesFetchRef.current = null;

          setThreads([]);

          if (selectedChannel === 'EMAIL') {
            const emailThreads = await fetchEmailThreads([inboxId]);
            if (isStale()) return; // Abort if request is stale
            setThreads(emailThreads);
          } else {
            // Fetch threads for a specific inbox based on selected channel
            const data = await service.getUnifiedThreads(
              inboxId,
              selectedChannel,
            );
            if (isStale()) return; // Abort if request is stale
            handleSetThreads(
              data.threads ? data.threads : data || [],
              selectedChannel,
            );
          }
        } else if (roleIdParam) {
          // Group view: the threads of every inbox the role grants on the
          // current channel.
          const role = permissions?.roles.find((r) => r.id === roleIdParam);

          // Permissions arrive once `user` is set; a run before that has
          // nothing to resolve and leaves the dedup key free for the next run.
          if (!role) return;

          const inboxesToFetch = getRoleInboxes(
            role,
            allInboxes.filter((ib) => ib.inboxType === selectedChannel),
          );

          // Skip if we've already fetched for this exact (roleId, channel) combo
          const fetchKey = `${roleIdParam}::${selectedChannel}`;
          if (lastAllInboxesFetchRef.current === fetchKey) {
            return;
          }
          lastAllInboxesFetchRef.current = fetchKey;

          setThreads([]);
          setRoleInboxes(inboxesToFetch);

          // If we have inboxes to fetch, get threads for all of them in one batch request
          if (inboxesToFetch.length > 0) {
            try {
              const inboxIds = inboxesToFetch.map((ib) => ib.id);

              if (selectedChannel === 'EMAIL') {
                // Email threads carry their own inboxId, and the endpoint takes
                // every inbox in one comma-separated filter.
                const emailThreads = await fetchEmailThreads(inboxIds);
                if (lastAllInboxesFetchRef.current !== fetchKey) return;
                setThreads(emailThreads);
                return;
              }

              const batchResult = await service.getBatchThreads(inboxIds, selectedChannel);

              if (lastAllInboxesFetchRef.current !== fetchKey) return;

              const threadsByInbox = batchResult.data || {};
              const combinedThreads = Object.entries(threadsByInbox).flatMap(
                ([ibId, threads]) =>
                  (Array.isArray(threads) ? threads : []).map((thread) => ({
                    ...thread,
                    inboxId: ibId,
                  })),
              );
              handleSetThreads(combinedThreads, selectedChannel);
            } catch (error) {
              console.error(
                `Error batch-fetching ${selectedChannel} threads:`,
                error,
              );
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        // Only update loading states if this request is still valid
        if (!isStale()) {
          setLoading(false);
          setThreadsLoading(false);
        }
      }
    };

    fetchData();
    // Only the inbox and role params drive a fetch; the conversation params are
    // read once on mount and would otherwise reload the list on every click.
  }, [inboxId, roleIdParam, user, selectedChannel, filteredInboxes.length]);

  // Filter inboxes based on permissions and channel type
  useEffect(() => {
    if (inboxes.length > 0 && userPermissions) {
      let filtered = filterInboxesByPermissions(
        inboxes,
        userPermissions,
        user?.id,
      );

      // Filter by inbox type based on selected channel
      if (selectedChannel === 'APP') {
        filtered = filtered.filter((inbox) => inbox.inboxType === 'APP');
      } else if (selectedChannel === 'SMS') {
        filtered = filtered.filter((inbox) => inbox.inboxType === 'SMS');
      } else if (selectedChannel === 'EMAIL') {
        filtered = filtered.filter((inbox) => inbox.inboxType === 'EMAIL');
      }

      setFilteredInboxes(filtered);

      // If current inbox is not in filtered list, select first available
      if (
        filtered.length > 0 &&
        inboxId &&
        !filtered.find((i) => i.id === inboxId) &&
        !selectedRoleId
      ) {
        const defaultInbox =
          filtered.find((i) => i.userId === user?.id) ?? filtered[0];
        setSearchParams({ inboxId: defaultInbox.id });
      }

      // Update role inboxes if a role is selected
      if (selectedRoleId) {
        const role = userPermissions.roles.find((g) => g.id === selectedRoleId);
        if (role) setRoleInboxes(getRoleInboxes(role, filtered));
      }
    } else {
      // Still filter by inbox type even without permissions
      let filtered = inboxes;
      if (selectedChannel === 'APP') {
        filtered = inboxes.filter((inbox) => inbox.inboxType === 'APP');
      } else if (selectedChannel === 'SMS') {
        filtered = inboxes.filter((inbox) => inbox.inboxType === 'SMS');
      } else if (selectedChannel === 'EMAIL') {
        filtered = inboxes.filter((inbox) => inbox.inboxType === 'EMAIL');
      }
      setFilteredInboxes(filtered);
    }
  }, [inboxes, userPermissions, user, selectedRoleId, selectedChannel]);

  // The "All inboxes" view is fetched by the main fetchData effect above, in
  // its roleIdParam branch.

  // Cleanup effect - runs on unmount
  useEffect(() => {
    return () => {
      setSmsThreadMessages([]);
    };
  }, [setSmsThreadMessages]);

  // Poll the current inbox view so inbound messages surface without a manual
  // refresh: refreshes the thread list (new threads appear) and keeps the open
  // thread's messages in sync. Covers SMS and APP; EMAIL has its own endpoint.
  useEffect(() => {
    if (selectedChannel === 'EMAIL') return;
    if (!inboxId && !selectedRoleId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        let freshThreads = [];

        if (inboxId) {
          const data = await service.getUnifiedThreads(
            inboxId,
            selectedChannel,
          );
          freshThreads = (data.threads ? data.threads : data) || [];
        } else if (selectedRoleId) {
          const inboxIds = roleInboxes.map((ib) => ib.id);
          if (inboxIds.length === 0) return;

          const batchResult = await service.getBatchThreads(
            inboxIds,
            selectedChannel,
          );
          const threadsByInbox = batchResult.data || {};
          freshThreads = Object.entries(threadsByInbox).flatMap(
            ([ibId, threads]) =>
              (Array.isArray(threads) ? threads : []).map((thread) => ({
                ...thread,
                inboxId: ibId,
              })),
          );
        }

        if (cancelled || freshThreads.length === 0) return;

        const sorted = handleSetThreads(freshThreads, selectedChannel);

        // Keep the open thread's messages in sync with the refreshed data.
        setSelectedFishThread((prev) => {
          if (!prev) return prev;
          const match = (sorted || []).find(
            (t) =>
              t.id === prev.id &&
              (t.inboxId ?? inboxId) === (prev.inboxId ?? inboxId),
          );
          if (!match) return prev;
          return { ...match, inboxId: prev.inboxId ?? inboxId };
        });
      } catch (error) {
        console.error('Error polling inbox threads:', error);
      }
    };

    const pollInterval = setInterval(poll, 8000);
    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [inboxId, selectedRoleId, selectedChannel, roleInboxes]);

  // =========================================================================
  // 5. EVENT HANDLERS
  // =========================================================================

  const handleSetThreads = (threads, channel = 'SMS') => {
    if (!threads || !Array.isArray(threads) || threads.length === 0) {
      setThreads([]);
      return [];
    }

    let sortedThreads;

    if (channel === 'APP') {
      // APP threads come from unified threads endpoint with different structure
      sortedThreads = threads.sort((a, b) => {
        const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return timeB - timeA;
      });
    } else {
      // SMS threads have SMSThread array
      sortedThreads = threads.sort((a, b) => {
        const timeA =
          a.SMSThread?.[a.SMSThread.length - 1]?.receivedAt ||
          a.SMSThread?.[a.SMSThread.length - 1]?.sentAt;
        const timeB =
          b.SMSThread?.[b.SMSThread.length - 1]?.receivedAt ||
          b.SMSThread?.[b.SMSThread.length - 1]?.sentAt;

        if (!timeA || !timeB) {
          return 0;
        }

        return new Date(timeB).getTime() - new Date(timeA).getTime();
      });
    }

    setThreads(sortedThreads);
    return sortedThreads;
  };

  // Ids with a mark-read request in flight. Focus and click both fire when the
  // send box is used, so the second call must not resend the same ids.
  const markingReadIdsRef = useRef(new Set());

  // Marks a thread's unread inbound messages read on the server and in local
  // state, clearing the thread list's unread highlight and the topbar count.
  const markThreadRead = async (thread) => {
    if (!thread) return;
    const threadInboxId = thread.inboxId || inboxId;

    let messagesKey;
    let isUnread;
    if (selectedChannel === 'APP') {
      messagesKey = 'messages';
      isUnread = (msg) => msg.to === threadInboxId && !msg.read;
    } else if (selectedChannel === 'SMS') {
      messagesKey = 'SMSThread';
      isUnread = (msg) => msg.sender && msg.read === false;
    } else {
      return;
    }

    const unreadMessageIds = (thread[messagesKey] || [])
      .filter(isUnread)
      .map((msg) => msg.id)
      .filter((id) => id && !markingReadIdsRef.current.has(id));
    if (unreadMessageIds.length === 0) return;

    unreadMessageIds.forEach((id) => markingReadIdsRef.current.add(id));
    const markLocal = (t) => {
      if (t.id !== thread.id || (t.inboxId || inboxId) !== threadInboxId) {
        return t;
      }
      return {
        ...t,
        ...(selectedChannel === 'APP' ? { unreadCount: 0 } : {}),
        [messagesKey]: (t[messagesKey] || []).map((msg) =>
          unreadMessageIds.includes(msg.id) ? { ...msg, read: true } : msg,
        ),
      };
    };

    try {
      await service.markMessagesAsRead(unreadMessageIds);
      setThreads((prevThreads) => prevThreads.map(markLocal));
      setSelectedFishThread((prev) => (prev ? markLocal(prev) : prev));
      queryClient.invalidateQueries({ queryKey: ['inbox-unread'] });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    } finally {
      unreadMessageIds.forEach((id) => markingReadIdsRef.current.delete(id));
    }
  };

  // Thread selection handlers
  const handleThreadSelect = async (thread) => {
    // Make sure the thread has an inboxId
    const threadWithInboxId = thread.inboxId
      ? thread
      : { ...thread, inboxId: inboxId };

    setSelectedFishThread(threadWithInboxId);
    setIsNewMessageOpen(false);

    // The email list query omits message bodies — pull the full conversation.
    if (selectedChannel === 'EMAIL' && !thread.isDraft) {
      try {
        const detail = await fetchEmailThreadDetail(thread.id);
        if (detail?.id) {
          setSelectedFishThread(detail);
        }
      } catch (error) {
        console.error('Error fetching email thread:', error);
      }
      return;
    }

    await markThreadRead(threadWithInboxId);
  };

  const handleNewThreadButton = () => {
    setIsNewMessageOpen(true);
    setSelectedFishThread(null);
  };

  // Fish selection handler for new messages
  const handleFishSelectChange = (selected) => {
    if (selected) {
      // Try to find existing thread
      const existingThread = threads.find(
        (thread) => thread.id === selected.value.id,
      );

      if (existingThread) {
        setSelectedFishThread({ ...existingThread, inboxId: inboxId });
      } else {
        // Get profile data if available
        const profile =
          selected.value.fishProfiles && selected.value.fishProfiles.length > 0
            ? selected.value.fishProfiles[0]
            : {};

        // Create a new thread object for this fish
        const newThread = {
          id: selected.value.id,
          fName: profile.fName || '',
          lName: profile.lName || '',
          phoneNumber: selected.value.phoneNumber,
          email: profile.email || selected.value.email || '',
          inboxId: inboxId, // Add the current inbox ID
          SMSThread: [], // Empty thread history
          fishProfiles: selected.value.fishProfiles, // Include fish profiles
          isDraft: true, // No conversation exists on the server yet
        };
        setSelectedFishThread(newThread);
      }
    }
  };

  /**
   * Reloads the email thread list and re-opens the conversation that was just
   * sent to. A reply keeps its thread; a brand new email produces one, and
   * since the list is ordered by most recent activity it is the first row.
   */
  const refreshEmailThreadsAfterSend = async () => {
    const inboxIds = inboxId
      ? [inboxId]
      : (roleInboxes.length > 0 ? roleInboxes : filteredInboxes).map(
          (ib) => ib.id,
        );

    const emailThreads = await fetchEmailThreads(inboxIds);
    setThreads(emailThreads);

    const updatedThread =
      emailThreads.find((t) => t.id === selectedFishThread?.id) ??
      emailThreads[0];

    if (!updatedThread) return;

    const detail = await fetchEmailThreadDetail(updatedThread.id);
    setSelectedFishThread(detail?.id ? detail : updatedThread);
  };

  // After message send handler
  const handleAfterSend = async () => {
    setIsNewMessageOpen(false);
    try {
      if (selectedChannel === 'EMAIL') {
        await refreshEmailThreadsAfterSend();
        return;
      }

      // Fetch the inbox details
      if (inboxId) {
        const inboxData = await service.readInbox(inboxId);
        setInbox(inboxData);

        // Fetch threads for this specific inbox based on selected channel
        let data;
        const result = await service.getUnifiedThreads(
          inboxId,
          selectedChannel,
        );
        data = result.threads ? result.threads : result || [];
        handleSetThreads(data, selectedChannel);

        // Find and set the selected thread in the updated list
        if (selectedFishThread) {
          // Add inboxId to each thread for unique identification
          const threadsWithInboxId = data.map((thread) => {
            return {
              ...thread,
              inboxId: inboxId,
            };
          });

          let updatedThread;
          if (selectedChannel === 'APP') {
            // For APP threads, try multiple matching strategies since local threads may not have server structure
            const targetFishId =
              selectedFishThread.otherParticipant?.fish?.id ||
              selectedFishThread.singleFish?.id ||
              selectedFishThread.id;

            updatedThread = threadsWithInboxId.find((thread) => {
              // Match by fish ID in otherParticipant
              if (thread.otherParticipant?.fish?.id === targetFishId)
                return true;
              // Match by thread ID (for existing threads)
              if (thread.id === selectedFishThread.id) return true;
              // Match by fish phone number as fallback
              if (
                selectedFishThread.phoneNumber &&
                thread.otherParticipant?.fish?.phoneNumber ===
                  selectedFishThread.phoneNumber
              )
                return true;
              return false;
            });
          } else {
            updatedThread = threadsWithInboxId.find(
              (thread) => thread.id === selectedFishThread.id,
            );
          }

          // If still not found, select the most recent thread (likely the one we just sent to)
          if (!updatedThread && threadsWithInboxId.length > 0) {
            updatedThread = threadsWithInboxId[0]; // Already sorted by lastMessageAt desc
          }

          if (updatedThread) {
            setSelectedFishThread(updatedThread);
          }
        }
      } else if (selectedRoleId) {
        // We have a role selected but no specific inbox (All inboxes view)

        // Determine which inboxes belong to this role
        let inboxesToFetch = [];

        // First try to use the roleInboxes state if it's already populated
        if (roleInboxes.length > 0) {
          inboxesToFetch = roleInboxes;
        }
        // If roleInboxes is empty but we have userPermissions, derive the inboxes directly
        else if (userPermissions) {
          const role = userPermissions.roles.find(
            (g) => g.id === selectedRoleId,
          );
          if (role) {
            inboxesToFetch = getRoleInboxes(role, filteredInboxes);
            setRoleInboxes(inboxesToFetch);
          }
        }

        // If we have inboxes to fetch, get threads for all of them
        if (inboxesToFetch.length > 0) {
          // Fetch threads for all inboxes in the role based on selected channel
          const allThreadsPromises = inboxesToFetch.map((roleInbox) => {
            return service
              .getUnifiedThreads(roleInbox.id, selectedChannel)
              .then((data) => {
                return (data.threads ? data.threads : data || []).map(
                  (thread) => ({
                    ...thread,
                    inboxId: roleInbox.id,
                  }),
                );
              })
              .catch((error) => {
                console.error(
                  `Error fetching ${selectedChannel} threads for inbox ${roleInbox.id}:`,
                  error,
                );
                return [];
              });
          });

          const allThreadsArrays = await Promise.all(allThreadsPromises);
          // Combine all threads into a single array
          const combinedThreads = allThreadsArrays.flat();
          handleSetThreads(combinedThreads, selectedChannel);

          // Find and set the selected thread in the updated list
          if (selectedFishThread) {
            let updatedThread;
            if (selectedChannel === 'APP') {
              // For APP threads, try multiple matching strategies
              const targetFishId =
                selectedFishThread.otherParticipant?.fish?.id ||
                selectedFishThread.singleFish?.id ||
                selectedFishThread.id;

              updatedThread = combinedThreads.find((thread) => {
                // Must match inboxId
                if (thread.inboxId !== selectedFishThread.inboxId) return false;
                // Match by fish ID in otherParticipant
                if (thread.otherParticipant?.fish?.id === targetFishId)
                  return true;
                // Match by thread ID (for existing threads)
                if (thread.id === selectedFishThread.id) return true;
                // Match by fish phone number as fallback
                if (
                  selectedFishThread.phoneNumber &&
                  thread.otherParticipant?.fish?.phoneNumber ===
                    selectedFishThread.phoneNumber
                )
                  return true;
                return false;
              });

              // If still not found, select most recent thread for this inbox
              if (!updatedThread) {
                updatedThread = combinedThreads.find(
                  (t) => t.inboxId === selectedFishThread.inboxId,
                );
              }
            } else {
              updatedThread = combinedThreads.find(
                (thread) =>
                  thread.id === selectedFishThread.id &&
                  thread.inboxId === selectedFishThread.inboxId,
              );
            }
            if (updatedThread) {
              setSelectedFishThread(updatedThread);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing data after send:', error);
    }
  };

  // =========================================================================
  // 6. CONVERSATION URL SYNC
  // =========================================================================

  // Mirror the open conversation into the URL so a reload lands back on it. The
  // thread's inbox is only recorded in the "all inboxes" view, where the same
  // id can belong to more than one inbox. Drafts have no server-side thread to
  // return to, so they are left out.
  useEffect(() => {
    const threadId = selectedFishThread?.isDraft
      ? null
      : (selectedFishThread?.id ?? null);
    const threadInboxId =
      threadId && !inboxId ? (selectedFishThread?.inboxId ?? null) : null;

    // Nothing is open yet on a page load that carries a conversation; leave the
    // params in place until the restore below has had its turn.
    if (!threadId && threadToRestoreRef.current.id) return;

    // Compare against and build from the browser URL, not `searchParams`: the
    // router applies navigations in a transition, so while a selector change is
    // pending `searchParams` still holds the previous view, and writing from it
    // would navigate back to that view.
    const current = new URLSearchParams(window.location.search);
    if (
      threadId === current.get('threadId') &&
      threadInboxId === current.get('threadInboxId')
    ) {
      return;
    }

    if (threadId) current.set('threadId', threadId);
    else current.delete('threadId');

    if (threadInboxId) current.set('threadInboxId', threadInboxId);
    else current.delete('threadInboxId');

    setSearchParams(current, { replace: true });
  }, [selectedFishThread, inboxId, searchParams, setSearchParams]);

  // Reopen the conversation carried in the URL as soon as the thread list
  // holding it has loaded. Threads are fetched per inbox and channel, so a link
  // to a conversation outside the current view never matches; either way the
  // restore is spent once a list has arrived, so that leaving a conversation is
  // not immediately undone.
  useEffect(() => {
    const pending = threadToRestoreRef.current;
    if (!pending.id || threads.length === 0) return;

    threadToRestoreRef.current = { id: null, inboxId: null };

    const thread = threads.find(
      (t) =>
        t.id === pending.id &&
        (!pending.inboxId || t.inboxId === pending.inboxId),
    );
    if (thread) handleThreadSelect(thread);
  }, [threads]);

  // =========================================================================
  // 7. CONTEXT VALUE
  // =========================================================================

  // Combine all the state and functions we want to expose
  const contextValue = {
    // State
    inbox,
    filteredInboxes,
    roleInboxes,
    threads: filteredThreads,
    selectedFishThread,
    searchQuery,
    loading,
    threadsLoading,
    isNewMessageOpen,
    selectedRoleId,
    inboxId,
    selectedChannel,

    // Derived data
    roles,
    inboxesByRole,
    individualInboxes,

    // Functions
    setInbox,
    setFilteredInboxes,
    setRoleInboxes,
    setThreads,
    setSelectedFishThread,
    setSearchQuery,
    setLoading,
    setIsNewMessageOpen,
    setSelectedRoleId,
    setSearchParams,
    setSelectedChannel,

    // Event handlers
    handleThreadSelect,
    markThreadRead,
    handleNewThreadButton,
    handleFishSelectChange,
    handleAfterSend,
  };

  return (
    <InboxContext.Provider value={contextValue}>
      {children}
    </InboxContext.Provider>
  );
};

// Custom hook to use the inbox context
export const useInbox = () => {
  const context = useContext(InboxContext);
  if (!context) {
    throw new Error('useInbox must be used within an InboxProvider');
  }
  return context;
};
